import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { createPanResponder } from 'helpers/pan-responder';
import { getClampedNextIndex, getClampedPrevIndex } from 'utils/gallery';
import scrollIntoView from 'scroll-into-view';
import { useTheme } from 'hooks/useTheme';

const VELOCITY_ANGLE_THRESHOLD = 0.7;
const DELTA_ANGLE_THRESHOLD = 0.4;
const DX_TERMINATE_THRESHOLD = 33;
const SWIPEX_THRESHOLD = 20;
const SWIPEX_SWITCH_THRESHOLD = 100;

export const SWIPE_TRANSITION_DURATION = 500;

export enum HighlightStatus {
  Active = 'active',
  Prev = 'prev',
  Next = 'next',
}

interface SwipableItemProps {
  swipeX: number;
  status: HighlightStatus;
  isTransitioning: boolean;
  topOffset: number;
}

export const SwipableItem = styled.div<SwipableItemProps>`
  position: relative;
  transition: opacity .5s ease;

  ${({ swipeX, status, isTransitioning, topOffset, theme: { isRTL } }) => {
    if (Math.abs(swipeX) > SWIPEX_THRESHOLD || isTransitioning) {
      const swipeXToOpacity = Math.min(1, Math.abs(swipeX) / (SWIPEX_SWITCH_THRESHOLD / 100) / 100);
      const maybeTransformTransition = isTransitioning ? `transform ${SWIPE_TRANSITION_DURATION}ms ease` : '';
      const calculatedOpacity = swipeXToOpacity - SWIPEX_THRESHOLD / 100;
      if (status === HighlightStatus.Active) {
        return `
          transition: opacity .2s ease, ${maybeTransformTransition};
          transform: translateX(${swipeX}px);
          opacity: ${1 - swipeXToOpacity};
        `;
      }
      else if (status === HighlightStatus.Next && (isRTL ? swipeX > 0 : swipeX < 0)) {
        const verticalOffset = topOffset - window.scrollY;
        return `
          transition: opacity .2s ease, ${maybeTransformTransition};
          opacity: ${calculatedOpacity};
          transform: translateX(calc(${isRTL ? '-' : ''}100% + ${swipeX}px)) translateY(-${verticalOffset}px);
        `;
      }
      else if (status === HighlightStatus.Prev && (isRTL ? swipeX < 0 : swipeX > 0)) {
        const verticalOffset = window.scrollY - topOffset;
        return `
          transition: opacity .2s ease, ${maybeTransformTransition};
          opacity: ${calculatedOpacity};
          transform: translateX(calc(${isRTL ? '' : '-'}100% + ${swipeX}px)) translateY(${verticalOffset}px);
        `;
      }
      else return `opacity: 0;`;
    }
    else {
      return '';
    }
  }}
`;

interface SwipableProps {
  children: JSX.Element[];
}

export const Swipable: React.FunctionComponent<SwipableProps> = ({
  children,
}) => {
  const [ swipeX, setSwipeX ] = useState(0);
  const [ isTransitioning, setIsTransitioning ] = useState(false);
  const swipedHighlightIndexRef = useRef(null);
  const rootRef = useRef(null);
  const isSwipeStarted = useRef({ isStarted: false });

  const { isRTL } = useTheme();

  const calcAngle = (x: number, y: number) => Math.atan(y / x);

  const animateSwipe = (onStart: () => void, onEnd: () => void) => {
    isSwipeStarted.current.isStarted = true;
    setIsTransitioning(true);
    onStart();
    setTimeout(() => {
      onEnd();
      isSwipeStarted.current.isStarted = false;
      swipedHighlightIndexRef.current = null;
      setIsTransitioning(false);
    }, SWIPE_TRANSITION_DURATION);
  };

  const cancelSwipe = () => {
    animateSwipe(
      () => setSwipeX(0),
      () => null
    );
  };

  const animateSwipeIndexChange = (nextIndex: number, latestSwipeX: number) => {
    animateSwipe(
      () => {
        setSwipeX(latestSwipeX < 0 ? -window.innerWidth : window.innerWidth);
      },
      () => {
        setSwipeX(0);
        swipedHighlightIndexRef.current = { id: nextIndex };
        scrollIntoView((rootRef.current.children[nextIndex] as HTMLElement), {
          time: 0,
          align: {
            top: 0,
          },
        });
      }
    );
  };

  const panHandlers = createPanResponder({
    shouldTerminate: (state, e) => {
      const angle = calcAngle(state.vx, state.vy);
      return Math.abs(angle) > VELOCITY_ANGLE_THRESHOLD && (Math.abs(state.dx) < DX_TERMINATE_THRESHOLD || !e.cancelable);
    },
    shouldPreventDefault: (state, e) => state.__granted && e.cancelable,
    shouldGrant: (state, e) => {
      const vAngle = calcAngle(state.vx, state.vy);
      const dAngle = calcAngle(state.dx, state.dy);
      return Math.abs(vAngle) < VELOCITY_ANGLE_THRESHOLD
        || Math.abs(dAngle) < DELTA_ANGLE_THRESHOLD
        || Math.abs(state.dx) > DX_TERMINATE_THRESHOLD;
    },
    onMove: (gestureState, e) => {
      if (gestureState.down === true) {
        if (Math.abs(gestureState.dx) > Math.abs(gestureState.dy)) {
          setSwipeX(Math.floor(gestureState.dx));
        }
      }
      else if (gestureState.down === false) {
        const latestSwipeX = Math.floor(gestureState.dx);
        if (Math.abs(latestSwipeX) < SWIPEX_THRESHOLD) return;

        if (Math.abs(latestSwipeX) > Math.abs(gestureState.dy) && Math.abs(latestSwipeX) >= SWIPEX_SWITCH_THRESHOLD) {
          const nextIndexFn = (isRTL ? (latestSwipeX > 0) : (latestSwipeX < 0)) ? getClampedNextIndex : getClampedPrevIndex;
          const nextIndex = nextIndexFn(swipedHighlightIndexRef.current.id, React.Children.count(children));

          if (nextIndex !== swipedHighlightIndexRef.current.id) {
            animateSwipeIndexChange(nextIndex, latestSwipeX);
          }
          else {
            cancelSwipe();
          }
        }
        else {
          cancelSwipe();
        }
      }
    },
    onStart: (gestureState, e) => {
      const swipedId = Array.from(rootRef.current.children).indexOf(e.currentTarget as HTMLElement);
      swipedHighlightIndexRef.current = { id: swipedId };
    },
    onEnd: (gestureState) => {
      return;
    },
  });

  const getHighlightStatus = (id: number) => {
    if (!swipedHighlightIndexRef.current) return null;
    if (swipedHighlightIndexRef.current.id === id) return HighlightStatus.Active;
    else if (id === getClampedNextIndex(swipedHighlightIndexRef.current.id, React.Children.count(children))) return HighlightStatus.Next;
    else if (id === getClampedPrevIndex(swipedHighlightIndexRef.current.id, React.Children.count(children))) return HighlightStatus.Prev;
    else return null;
  };

  return (
    <div ref={rootRef}>
      {React.Children.map(children, (child, id) => {
        const status = getHighlightStatus(id);
        const highlightEl = rootRef.current && (rootRef.current.children[id] as HTMLElement);
        return (
          <SwipableItem
            key={id}
            status={status}
            swipeX={status !== null ? swipeX : null}
            isTransitioning={status !== null ? isTransitioning : false}
            topOffset={highlightEl && highlightEl.offsetTop}
            {...(isSwipeStarted.current.isStarted ? {} : panHandlers)}
          >
            {child}
          </SwipableItem>
        );
      })}
    </div>
  );
};
